import { useState, useCallback, useEffect } from "react";
import { Button, Label } from "InnoUI/components";
import theme from "theme";
import BulkActionView from "InnoUI/components/BulkActionView";
import { InputField } from "components";
import { Chip, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { validateEmail, validtionMessages } from "utils/config";
import SC from "./style";
import { showAlert } from "utils/helper";

const sendNotificationTo = [
  { id: 1, label: "To", key: "sendTo" },
  { id: 2, label: "Cc", key: "sendCc" },
  { id: 3, label: "Bcc", key: "sendBcc" },
];

const AddAdditionalEmail = ({
  setOpenEmailModalView,
  openEmailModalView,
  data,
  setData = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [errorKeys, setErrorKeys] = useState({ additionalEmail: false });
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    setData((prevData) => ({
      ...prevData,
      sendEmail: 1,
    }));
  }, []);

  const handleChange = (property, value) => {
    if (property === "sendEmail") {
      setData((prevData) => ({ ...prevData, sendEmail: value }));
    }
  };

  const removeEmail = (emailToRemove) => {
    setData((prevData) => ({
      ...prevData,
      additionalEmail: Array.isArray(prevData.additionalEmail)
        ? prevData.additionalEmail.filter((email) => email !== emailToRemove)
        : [],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedInput = inputValue.trim().toLowerCase();
    const currentEmails = Array.isArray(data.additionalEmail)
      ? [...data.additionalEmail]
      : [];

    // Add the last valid input (if not already committed via Enter)
    if (trimmedInput && validateEmail(trimmedInput)) {
      currentEmails.push(trimmedInput);
    }

    if (currentEmails.length === 0) {
      showAlert({title: "Error", text: "Please enter a valid email.", icon: "error"});

      return;
    }

    const selectedOption = sendNotificationTo.find(
      (item) => item.id === Number(data?.sendEmail)
    );

    if (!selectedOption) return;

    const getEmails = (val) =>
      typeof val === "string"
        ? val.split(",").map((e) => e.trim().toLowerCase())
        : Array.isArray(val)
        ? val.map((e) => e.trim().toLowerCase())
        : [];

    const existingEmails = getEmails(data[selectedOption.key]);
    const additionalKey = `AdditionalEmail${selectedOption.label}`;
    const existingAdditionalEmails = getEmails(data[additionalKey]);

    const allExistingEmails = new Set([
      ...getEmails(data.sendTo),
      ...getEmails(data.sendCc),
      ...getEmails(data.sendBcc),
      ...existingEmails,
      ...existingAdditionalEmails,
    ]);

    const duplicates = currentEmails.filter((email) =>
      allExistingEmails.has(email)
    );

    if (duplicates.length > 0) {
     
      showAlert({
        title: "Error",
        text: `Email(s) already exist: ${duplicates.join(", ")}`,
        icon: "error",
      });
      return;
    }

    const mergedMain = [...new Set([...existingEmails, ...currentEmails])];
    const mergedAdditional = [
      ...new Set([...existingAdditionalEmails, ...currentEmails]),
    ];

    setData((prevData) => ({
      ...prevData,
      [selectedOption.key]: mergedMain.join(", "),
      [additionalKey]: mergedAdditional,
      additionalEmail: [],
    }));

    setInputValue("");
    setOpenEmailModalView(false);
  };

  const handleKeyDown = (e) => {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      const newEmail = inputValue.trim().toLowerCase();

      if (newEmail && validateEmail(newEmail)) {
        setData((prevData) => ({
          ...prevData,
          additionalEmail: [
            ...(Array.isArray(prevData.additionalEmail)
              ? prevData.additionalEmail
              : []),
            newEmail,
          ],
        }));
        setInputValue("");
        setErrorKeys((prevKeys) => ({ ...prevKeys, additionalEmail: false }));
      } else {
        setErrorKeys((prevKeys) => ({ ...prevKeys, additionalEmail: true }));
      }
    }
  };

  const handleCancelModal = useCallback(() => {
    setOpenEmailModalView(false);
    setData((prevData) => ({
      ...prevData,
      additionalEmail: "",
    }));
  }, [setOpenEmailModalView, setData]);

  const AddModalData = () => (
    <form onSubmit={handleSubmit} style={{ marginTop: "30px" }}>
      <Label label="Create to">
        <RadioGroup
          row
          name="sendEmail"
          value={data?.sendEmail || ""}
          onChange={(e) => handleChange("sendEmail", e.target.value)}
        >
          {sendNotificationTo.map(({ id, label }) => (
            <FormControlLabel
              key={id}
              value={id}
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                    "&.Mui-checked": { color: "#EC810B" },
                    margin: "0px 0px 0px 10px",
                  }}
                />
              }
              label={label}
            />
          ))}
        </RadioGroup>
      </Label>
      <span style={{ fontSize: "12px", padding: "0px 0px 0px 15px" }}>
        Please enter the email address followed by a comma or press Enter.
      </span>
      <SC.LabelContainer>
        <SC.Label>
          Email <SC.Astrik>*</SC.Astrik>
        </SC.Label>
        <SC.Children>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          >
            {Array.isArray(data?.additionalEmail) &&
              data.additionalEmail.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  variant="outlined"
                  onDelete={() => removeEmail(email)}
                  sx={{
                    margin: "2px",
                    backgroundColor: "#f0f0f0",
                    color: "#333",
                    fontSize: "12px",
                    height: "25px",
                  }}
                />
              ))}

            <InputField
              name="additionalEmail"
              type="email"
              placeholder="Enter email addresses, separated by commas"
              value={inputValue}
              onChange={(_, value) => setInputValue(value)}
              onKeyDown={handleKeyDown}
              style={{ border: "none", outline: "none", flex: 1 }}
            />
          </div>
          {errorKeys.additionalEmail && (
            <SC.ValidtionMessages>
              {validtionMessages.email}
            </SC.ValidtionMessages>
          )}
        </SC.Children>
      </SC.LabelContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          margin: "20px",
        }}
      >
        <Button
          caption="Cancel"
          onClick={handleCancelModal}
          fontSize="12px"
          style={{
            background: theme.colors.cancelButton,
            color: theme.colors.white,
            minWidth: "90px",
            borderRadius: "3px",
            height: "30px",
          }}
        />

        <Button
          caption="Create"
          isLoading={loading}
          type="submit"
          color="white"
          fontSize="12px"
          style={{
            background: theme.gradient.themeGradient,
            color: theme.colors.white,
            minWidth: "90px",
            height: "30px",
            borderRadius: "3px",
          }}
        />
      </div>
    </form>
  );

  return (
    openEmailModalView && (
      <BulkActionView
        title="Add Email Only Contact"
        openModal={openEmailModalView}
        renderBodyConditionally={AddModalData}
        handleCloseModal={handleCancelModal}
        sx={{
          padding: "20px",
        }}
      />
    )
  );
};

export default AddAdditionalEmail;
